import type { LevelDef } from "building-types/levels/LevelDef";
import type { RWTable, Table } from "common/data/table";
import { ensureDefined } from "common/utils/assertOfType";
import { isDatumLevelDef, levelDefHasManualStackAbove, levelDefHasStackAbove } from "./levelTools";

/**
 * Dependency graph for tracking level generation dependencies
 */
export interface LevelDependencyGraph {
  /** Table of level ID to its dependencies (levels it depends on) */
  readonly dependencies: Table<Set<string>>;
  /** Table of level ID to levels that depend on it */
  readonly dependents: Table<Set<string>>;
}

/**
 * Result of topological sort with circular dependency detection
 */
export interface TopologicalLevelSortResult {
  /** Ordered list of level IDs in generation order */
  readonly order: string[];
  /** Circular dependency chains if detected */
  readonly circularDependencies: string[][];
}

/**
 * Build dependency graph for level generation
 * Resolves transitive dependencies through disabled levels
 *
 * @param activeLevelDefs - Only the active (non-disabled) level definitions
 * @param allLevelDefs - All level definitions (including disabled ones) for resolving references
 */
export function buildLevelDependencyGraph(
  activeLevelDefs: Table<LevelDef>,
  allLevelDefs: Table<LevelDef>,
): LevelDependencyGraph {
  const dependencies: RWTable<Set<string>> = {};
  const dependents: RWTable<Set<string>> = {};

  // Initialize tables for active levels only
  for (const levelId in activeLevelDefs) {
    dependencies[levelId] = new Set<string>();
    dependents[levelId] = new Set<string>();
  }

  /**
   * Resolve stackAbove through disabled levels to find the first active level
   * Returns undefined if no active level is found in the chain
   */
  const resolveActiveStackAbove = (
    stackAboveId: string | undefined,
    visited: Set<string> = new Set(),
  ): string | undefined => {
    if (!stackAboveId) {
      return undefined;
    }

    // Prevent infinite loops from circular references
    if (visited.has(stackAboveId)) {
      return undefined;
    }

    // If the level is active, we found our target
    if (activeLevelDefs[stackAboveId]) {
      return stackAboveId;
    }

    // If the level doesn't exist at all, stop
    const stackAboveDef = allLevelDefs[stackAboveId];
    if (!stackAboveDef) {
      return undefined;
    }

    // Level exists but is disabled - continue through its stackAbove
    visited.add(stackAboveId);
    if (levelDefHasStackAbove(stackAboveDef)) {
      return resolveActiveStackAbove(stackAboveDef.placement.stackAbove, visited);
    }

    // Disabled level doesn't have explicit stacking, can't resolve further.
    return undefined;
  };

  // Build dependency edges, resolving through disabled levels
  // NOTE: Only stackAbove affects generation order. The aboveLevelId/belowLevelId
  // relationships from datum placements are metadata only and don't affect generation order.
  for (const levelId in activeLevelDefs) {
    const levelDef = activeLevelDefs[levelId];
    const levelDeps = dependencies[levelId];

    // Levels with stackAbove depend on that level for generation order.
    if (levelDefHasStackAbove(levelDef)) {
      const stackAboveId = levelDef.placement.stackAbove;
      const resolvedStackAboveId = resolveActiveStackAbove(stackAboveId);

      if (resolvedStackAboveId) {
        levelDeps.add(resolvedStackAboveId);
        dependents[resolvedStackAboveId].add(levelId);
      }
    }
  }

  return { dependencies, dependents };
}

/**
 * Build a relationship graph for detecting circular dependencies in level relationships.
 * This includes all relationship types: stackAbove, aboveLevelId, and belowLevelId.
 * Unlike buildLevelDependencyGraph, this is NOT used for generation order.
 *
 * @param levelDefs - Level definitions to analyze
 * @returns A dependency graph for cycle detection purposes only
 */
export function buildLevelRelationshipGraph(levelDefs: Table<LevelDef>): LevelDependencyGraph {
  const dependencies: RWTable<Set<string>> = {};
  const dependents: RWTable<Set<string>> = {};

  // Filter to active levels only
  const activeLevelDefs: RWTable<LevelDef> = {};
  for (const levelId in levelDefs) {
    if (!levelDefs[levelId].disabled) {
      activeLevelDefs[levelId] = levelDefs[levelId];
    }
  }

  // Initialize tables for active levels only
  for (const levelId in activeLevelDefs) {
    dependencies[levelId] = new Set<string>();
    dependents[levelId] = new Set<string>();
  }

  // Build edges for all relationship types
  // We use a consistent direction: edges point from lower level to higher level
  // This way, a cycle indicates contradictory relationships
  for (const levelId in activeLevelDefs) {
    const levelDef = activeLevelDefs[levelId];
    const levelDeps = dependencies[levelId];

    // Manually stacked levels: A stacks on B means A is above B
    // Edge: B -> A (B is below A)
    if (levelDefHasManualStackAbove(levelDef)) {
      const stackAbove = levelDef.placement.stackAbove;
      if (stackAbove && activeLevelDefs[stackAbove]) {
        levelDeps.add(stackAbove);
        dependents[stackAbove].add(levelId);
      }
    }

    // Datum placement relationships
    if (isDatumLevelDef(levelDef)) {
      const { aboveLevelId, belowLevelId } = levelDef.placement;

      // aboveLevelId: "B is above me" means this level (A) is below B
      // Edge: A -> B (A is below B)
      if (aboveLevelId && activeLevelDefs[aboveLevelId]) {
        levelDeps.add(aboveLevelId);
        dependents[aboveLevelId].add(levelId);
      }

      // belowLevelId: "B is below me" means this level (A) is above B
      // Edge: B -> A (B is below A)
      if (belowLevelId && activeLevelDefs[belowLevelId]) {
        dependencies[belowLevelId].add(levelId);
        dependents[levelId].add(belowLevelId);
      }
    }
  }

  return { dependencies, dependents };
}

/**
 * Detect circular dependencies in the dependency graph
 */
export function detectLevelCircularDependencies(graph: LevelDependencyGraph): string[][] {
  const cycles: string[][] = [];
  const visiting = new Set<string>();
  const visited = new Set<string>();

  function dfs(levelId: string, path: string[]): void {
    if (visiting.has(levelId)) {
      // Found a cycle - extract the cycle from the path
      const cycleStart = path.indexOf(levelId);
      if (cycleStart !== -1) {
        cycles.push([...path.slice(cycleStart), levelId]);
      }
      return;
    }

    if (visited.has(levelId)) {
      return;
    }

    visiting.add(levelId);
    path.push(levelId);

    const deps = graph.dependencies[levelId];
    if (deps) {
      for (const depId of deps) {
        dfs(depId, path);
      }
    }

    path.pop();
    visiting.delete(levelId);
    visited.add(levelId);
  }

  const levelIds = Object.keys(graph.dependencies);
  for (const levelId of levelIds) {
    if (!visited.has(levelId)) {
      dfs(levelId, []);
    }
  }

  return cycles;
}

/**
 * Topological sort using Kahn's algorithm
 * Returns generation order and any circular dependencies found
 */
export function topologicalLevelSort(
  levelDefs: Table<LevelDef>,
  graph: LevelDependencyGraph,
): TopologicalLevelSortResult {
  const circularDependencies = detectLevelCircularDependencies(graph);

  if (circularDependencies.length > 0) {
    return { order: [], circularDependencies };
  }

  // Kahn's algorithm
  const inDegree: RWTable<number> = {};
  const queue: string[] = [];

  // Calculate in-degrees
  const levelIds = Object.keys(graph.dependencies);
  for (const levelId of levelIds) {
    inDegree[levelId] = graph.dependencies[levelId].size;
  }

  // Add all nodes with in-degree 0 to queue, sorted by sortIndex for deterministic ordering
  const zeroInDegreeNodes: Array<{ id: string; sortIndex: number }> = [];
  const inDegreeKeys = Object.keys(inDegree);
  for (const levelId of inDegreeKeys) {
    const degree = inDegree[levelId];
    if (degree === 0) {
      zeroInDegreeNodes.push({ id: levelId, sortIndex: levelDefs[levelId].sortIndex });
    }
  }
  zeroInDegreeNodes.sort((a, b) => a.sortIndex - b.sortIndex);
  queue.push(...zeroInDegreeNodes.map((n) => n.id));

  const order: string[] = [];

  while (queue.length > 0) {
    const levelId = ensureDefined(queue.shift());
    order.push(levelId);

    // Reduce in-degree for all dependents
    const deps = graph.dependents[levelId];
    if (deps) {
      const nextBatch: Array<{ id: string; sortIndex: number }> = [];
      for (const depId of deps) {
        const newDegree = inDegree[depId] - 1;
        inDegree[depId] = newDegree;
        if (newDegree === 0) {
          nextBatch.push({ id: depId, sortIndex: levelDefs[depId].sortIndex });
        }
      }
      // Sort by sortIndex before adding to queue for deterministic ordering
      nextBatch.sort((a, b) => a.sortIndex - b.sortIndex);
      queue.push(...nextBatch.map((n) => n.id));
    }
  }

  return { order, circularDependencies: [] };
}

/**
 * Type of level relationship for cycle checking
 */
export type LevelRelationshipType = "aboveLevelId" | "belowLevelId" | "stackAbove";

/**
 * Check if adding a level relationship would create a circular dependency
 *
 * @param levels - Current level definitions
 * @param sourceLevelId - The level being modified
 * @param relationshipType - The type of relationship being added
 * @param targetLevelId - The level being referenced
 * @returns true if adding this relationship would create a cycle
 */
export function wouldCreateLevelCycle(
  levels: Table<LevelDef>,
  sourceLevelId: string,
  relationshipType: LevelRelationshipType,
  targetLevelId: string,
): boolean {
  // Can't reference self
  if (sourceLevelId === targetLevelId) {
    return true;
  }

  // Build a simplified dependency graph including the proposed change
  // We only need active (non-disabled) levels
  const activeLevels: RWTable<LevelDef> = {};
  for (const levelId in levels) {
    if (!levels[levelId].disabled) {
      activeLevels[levelId] = levels[levelId];
    }
  }

  // Both source and target must be active
  if (!activeLevels[sourceLevelId] || !activeLevels[targetLevelId]) {
    return false;
  }

  // Build adjacency list for cycle detection
  const edges: RWTable<Set<string>> = {};
  for (const levelId in activeLevels) {
    edges[levelId] = new Set<string>();
  }

  // Add existing manually stacking and datum relationship edges
  for (const levelId in activeLevels) {
    const levelDef = activeLevels[levelId];

    // Manually stacked levels
    if (levelDefHasManualStackAbove(levelDef)) {
      const stackAbove = levelDef.placement.stackAbove;
      if (stackAbove && activeLevels[stackAbove]) {
        if (!(levelId === sourceLevelId && relationshipType === "stackAbove")) {
          edges[levelId].add(stackAbove);
        }
      }
    }

    // Datum placement relationships
    if (isDatumLevelDef(levelDef)) {
      const { aboveLevelId, belowLevelId } = levelDef.placement;

      if (aboveLevelId && activeLevels[aboveLevelId]) {
        if (!(levelId === sourceLevelId && relationshipType === "aboveLevelId")) {
          edges[levelId].add(aboveLevelId);
        }
      }

      if (belowLevelId && activeLevels[belowLevelId]) {
        if (!(levelId === sourceLevelId && relationshipType === "belowLevelId")) {
          edges[belowLevelId].add(levelId);
        }
      }
    }
  }

  // Add implicit edges for automatic stacking levels.
  //
  // In generation, auto-stacking assigns levelBelow = lastPlacedLevelId, where
  // lastPlacedLevelId tracks the previous level in generation order. Generation
  // order is a topological sort of explicit stacking dependencies with sortIndex
  // tie-breaking. We must replicate that order here — using raw sortIndex
  // adjacency is wrong when explicit stacking reorders levels relative to
  // sortIndex (e.g., a lower-sortIndex level explicitly stacking above a
  // higher-sortIndex level gets deferred in generation order, shifting which
  // level an auto-stacking level actually stacks above).
  const explicitStackingDependencies: RWTable<Set<string>> = {};
  const explicitStackingDependents: RWTable<Set<string>> = {};
  for (const levelId in activeLevels) {
    explicitStackingDependencies[levelId] = new Set<string>();
    explicitStackingDependents[levelId] = new Set<string>();
  }

  for (const levelId in activeLevels) {
    const levelDef = activeLevels[levelId];
    if (levelDefHasManualStackAbove(levelDef)) {
      const stackAbove = levelDef.placement.stackAbove;
      if (stackAbove && activeLevels[stackAbove]) {
        if (!(levelId === sourceLevelId && relationshipType === "stackAbove")) {
          explicitStackingDependencies[levelId].add(stackAbove);
          explicitStackingDependents[stackAbove].add(levelId);
        }
      }
    }
  }

  // When proposing a stackAbove, include it in the generation order computation
  if (relationshipType === "stackAbove") {
    explicitStackingDependencies[sourceLevelId].add(targetLevelId);
    explicitStackingDependents[targetLevelId].add(sourceLevelId);
  }

  const explicitGraph: LevelDependencyGraph = {
    dependencies: explicitStackingDependencies,
    dependents: explicitStackingDependents,
  };
  const { order: generationOrder, circularDependencies: explicitCycles } = topologicalLevelSort(
    activeLevels,
    explicitGraph,
  );

  if (explicitCycles.length > 0) {
    return true;
  }

  // Walk generation order to add auto-stacking edges, mirroring how
  // generateLevels assigns levelBelow = lastPlacedLevelId for auto-stacking
  // levels. Every level (including datum) updates lastPlacedLevelId.
  let lastPlacedLevelId: string | undefined = undefined;
  for (const levelId of generationOrder) {
    const levelDef = activeLevels[levelId];
    const isStackedLevel = levelDef.placement.type === "STACKED" && "automaticStacking" in levelDef.placement;
    const hasAutomaticStacking = isStackedLevel && levelDef.placement.automaticStacking !== false;
    const isReplacingAutomaticStacking = levelId === sourceLevelId && relationshipType === "stackAbove";

    if (hasAutomaticStacking && !isReplacingAutomaticStacking && lastPlacedLevelId) {
      edges[lastPlacedLevelId].add(levelId);
    }

    lastPlacedLevelId = levelId;
  }

  // Add the proposed relationship
  switch (relationshipType) {
    case "aboveLevelId":
    case "stackAbove":
      // Source level depends on target level
      edges[sourceLevelId].add(targetLevelId);
      break;
    case "belowLevelId":
      // Target level depends on source level
      edges[targetLevelId].add(sourceLevelId);
      break;
  }

  // Check for cycles using DFS from the source level
  // We need to check if there's a path from targetLevelId back to sourceLevelId
  // (for aboveLevelId/stackAbove) or from sourceLevelId back to targetLevelId (for belowLevelId)
  const visited = new Set<string>();
  const visiting = new Set<string>();

  function hasCycle(levelId: string): boolean {
    if (visiting.has(levelId)) {
      return true;
    }
    if (visited.has(levelId)) {
      return false;
    }

    visiting.add(levelId);

    const deps = edges[levelId];
    if (deps) {
      for (const depId of deps) {
        if (hasCycle(depId)) {
          return true;
        }
      }
    }

    visiting.delete(levelId);
    visited.add(levelId);
    return false;
  }

  // Check all nodes for cycles
  for (const levelId in edges) {
    if (!visited.has(levelId)) {
      if (hasCycle(levelId)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Get all level IDs that would NOT create a cycle if selected for a given relationship
 *
 * @param levels - Current level definitions
 * @param sourceLevelId - The level being modified
 * @param relationshipType - The type of relationship being added
 * @returns Array of level IDs that are valid choices (won't create a cycle)
 */
export function getValidLevelRelationshipTargets(
  levels: Table<LevelDef>,
  sourceLevelId: string,
  relationshipType: LevelRelationshipType,
): string[] {
  const validTargets: string[] = [];

  for (const levelId in levels) {
    // Skip self
    if (levelId === sourceLevelId) {
      continue;
    }

    // Skip disabled levels
    if (levels[levelId].disabled) {
      continue;
    }

    // Check if this would create a cycle
    if (!wouldCreateLevelCycle(levels, sourceLevelId, relationshipType, levelId)) {
      validTargets.push(levelId);
    }
  }

  return validTargets;
}
